import React, { useCallback, useEffect, useRef, useState } from "react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Camera, Image as ImageIcon, Minus, Plus, X, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import {
  SPECIES,
  fishReaction,
  LOADING_LINES,
  computeLeaders,
  catchSaveToast,
} from "@/lib/helpers";
import { processImageToBase64 } from "@/lib/photo";

// Photo processing lives in /app/frontend/src/lib/photo.js

export default function AddCatchDrawer({ open, onOpenChange }) {
  const { members, activeUser, catches, setCatches, refresh } = useApp();
  const [photo, setPhoto] = useState(null);
  const [length, setLength] = useState(25);
  const [ownerId, setOwnerId] = useState(activeUser?.id || null);
  const [species, setSpecies] = useState("");
  const [saving, setSaving] = useState(false);
  const [savingLine, setSavingLine] = useState(LOADING_LINES[0]);
  const [processing, setProcessing] = useState(false);
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const holdTimer = useRef(null);
  const holdInterval = useRef(null);

  // Reset when opened
  useEffect(() => {
    if (open) {
      setPhoto(null);
      setLength(25);
      setOwnerId(activeUser?.id || members[0]?.id || null);
      setSpecies("");
    }
  }, [open, activeUser, members]);

  // Rotate loading lines while saving
  useEffect(() => {
    if (!saving) return;
    let i = 0;
    const t = setInterval(() => {
      i = (i + 1) % LOADING_LINES.length;
      setSavingLine(LOADING_LINES[i]);
    }, 700);
    return () => clearInterval(t);
  }, [saving]);

  const onPhotoChange = async (e) => {
    const file = e.target.files?.[0];
    // Reset so picking the same file again re-triggers change
    e.target.value = "";
    if (!file) return;
    setProcessing(true);
    try {
      const dataUrl = await processImageToBase64(file);
      setPhoto(dataUrl);
    } catch (err) {
      console.error(err);
      toast.error("Photo failed. Try again.");
    } finally {
      setProcessing(false);
    }
  };

  const startHold = (delta) => {
    // First bump immediately
    setLength((v) => Math.max(1, v + delta));
    // After 400ms, start rapid increment
    holdTimer.current = setTimeout(() => {
      holdInterval.current = setInterval(() => {
        setLength((v) => Math.max(1, v + delta));
      }, 55);
    }, 350);
  };

  const stopHold = useCallback(() => {
    if (holdTimer.current) clearTimeout(holdTimer.current);
    if (holdInterval.current) clearInterval(holdInterval.current);
    holdTimer.current = null;
    holdInterval.current = null;
  }, []);

  useEffect(() => () => stopHold(), [stopHold]);

  const save = async () => {
    if (!ownerId) {
      toast.error("Pick an angler.");
      return;
    }
    if (!activeUser) {
      toast.error("Nobody active. Weird.");
      return;
    }
    setSaving(true);
    try {
      const prevLeaders = computeLeaders(catches, members);
      const wasFirst = !catches.some((c) => c.catch_owner_id === ownerId);
      const created = await api.addCatch({
        catch_owner_id: ownerId,
        entered_by_user_id: activeUser.id,
        length_cm: Number(length),
        species: species || "Other",
        photo_base64: photo,
      });
      const nextCatches = [created, ...catches];
      setCatches(nextCatches);
      const nextLeaders = computeLeaders(nextCatches, members);
      const owner = members.find((m) => m.id === ownerId);
      const t = catchSaveToast({
        member: owner,
        cm: Number(length),
        prev: prevLeaders,
        next: nextLeaders,
        wasFirst,
      });
      toast(t.title, { description: t.desc });
      if (!species) {
        setTimeout(() => {
          toast("No species picked. We'll call it 'a fish'.");
        }, 800);
      }
      onOpenChange(false);
      // background refresh
      refresh();
    } catch (e) {
      toast.error("Save failed. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const reaction = fishReaction(length);
  const owner = members.find((m) => m.id === ownerId);

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="bg-[#FAF8F3] max-h-[92vh]">
        <DrawerHeader className="pb-2">
          <DrawerTitle className="font-display text-2xl text-[#0A3D62]">
            New catch 🎣
          </DrawerTitle>
        </DrawerHeader>

        <div className="px-5 pb-24 space-y-5 overflow-y-auto" data-testid="add-catch-form">
          {/* Photo capture */}
          <div>
            <input
              ref={cameraRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={onPhotoChange}
              data-testid="add-catch-camera-input"
            />
            <input
              ref={galleryRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={onPhotoChange}
              data-testid="add-catch-gallery-input"
            />
            {photo ? (
              <div className="relative w-full aspect-[4/3] rounded-3xl overflow-hidden" data-testid="add-catch-photo-preview">
                <img src={photo} alt="" className="w-full h-full object-cover" />
                <div className="absolute bottom-3 left-3 right-3 flex gap-2">
                  <button
                    onClick={() => cameraRef.current?.click()}
                    className="flex-1 bg-white/95 backdrop-blur text-[#0A3D62] rounded-xl px-3 py-2 font-bold text-sm flex items-center justify-center gap-1.5 active:scale-95"
                    data-testid="add-catch-retake-camera"
                  >
                    <RefreshCw className="w-3.5 h-3.5" strokeWidth={2.5} /> Retake
                  </button>
                  <button
                    onClick={() => galleryRef.current?.click()}
                    className="flex-1 bg-white/95 backdrop-blur text-[#0A3D62] rounded-xl px-3 py-2 font-bold text-sm flex items-center justify-center gap-1.5 active:scale-95"
                    data-testid="add-catch-repick-gallery"
                  >
                    <ImageIcon className="w-3.5 h-3.5" strokeWidth={2.5} /> Library
                  </button>
                </div>
                <button
                  onClick={() => setPhoto(null)}
                  className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/60 text-white flex items-center justify-center"
                  aria-label="Clear photo"
                  data-testid="add-catch-clear-photo"
                >
                  <X className="w-4 h-4" strokeWidth={2.5} />
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => cameraRef.current?.click()}
                  data-testid="add-catch-camera-btn"
                  disabled={processing}
                  className="aspect-[4/5] rounded-3xl border-2 border-dashed border-[#0A3D62]/25 bg-white flex flex-col items-center justify-center gap-2 active:scale-[0.99] transition disabled:opacity-60"
                >
                  <Camera className="w-10 h-10 text-[#0A3D62]/70" strokeWidth={2} />
                  <div className="font-display text-base font-black text-[#0A3D62]">Take photo</div>
                  <div className="text-[10px] text-[#556B7D] uppercase tracking-widest">Camera</div>
                </button>
                <button
                  onClick={() => galleryRef.current?.click()}
                  data-testid="add-catch-gallery-btn"
                  disabled={processing}
                  className="aspect-[4/5] rounded-3xl border-2 border-dashed border-[#0A3D62]/25 bg-white flex flex-col items-center justify-center gap-2 active:scale-[0.99] transition disabled:opacity-60"
                >
                  <ImageIcon className="w-10 h-10 text-[#0A3D62]/70" strokeWidth={2} />
                  <div className="font-display text-base font-black text-[#0A3D62]">From roll</div>
                  <div className="text-[10px] text-[#556B7D] uppercase tracking-widest">Gallery</div>
                </button>
              </div>
            )}
            {processing && (
              <div className="text-center text-xs text-[#556B7D] mt-2 font-semibold" data-testid="photo-processing">
                Squeezing that photo down…
              </div>
            )}
          </div>

          {/* Length */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase font-bold text-[#556B7D]">Length</div>
              <div className="text-xs font-bold px-2 py-0.5 rounded-md" style={{ background: reaction.color + "22", color: reaction.color }}>
                {reaction.label}
              </div>
            </div>
            <div className="flex items-center justify-between bg-white rounded-2xl p-3 border border-[#0A3D62]/10">
              <button
                data-testid="add-catch-length-minus"
                onMouseDown={() => startHold(-1)}
                onMouseUp={stopHold}
                onMouseLeave={stopHold}
                onTouchStart={(e) => {
                  e.preventDefault();
                  startHold(-1);
                }}
                onTouchEnd={stopHold}
                onTouchCancel={stopHold}
                className="w-16 h-16 rounded-2xl bg-[#0A3D62] text-white flex items-center justify-center active:scale-95 select-none"
              >
                <Minus className="w-7 h-7" strokeWidth={3} />
              </button>
              <div className="text-center select-none" data-testid="add-catch-length-display">
                <div className="font-display text-6xl font-black text-[#0A3D62] leading-none">
                  {length}
                </div>
                <div className="text-xs uppercase tracking-widest font-bold text-[#556B7D] mt-1">
                  centimetres
                </div>
              </div>
              <button
                data-testid="add-catch-length-plus"
                onMouseDown={() => startHold(1)}
                onMouseUp={stopHold}
                onMouseLeave={stopHold}
                onTouchStart={(e) => {
                  e.preventDefault();
                  startHold(1);
                }}
                onTouchEnd={stopHold}
                onTouchCancel={stopHold}
                className="w-16 h-16 rounded-2xl bg-[#E8A838] text-[#1A2A35] flex items-center justify-center active:scale-95 select-none"
              >
                <Plus className="w-7 h-7" strokeWidth={3} />
              </button>
            </div>
          </div>

          {/* Angler */}
          <div>
            <div className="text-xs uppercase font-bold text-[#556B7D] mb-2">
              Angler {owner && <span className="text-[#0A3D62] normal-case font-semibold">— {owner.name}</span>}
            </div>
            <div className="flex gap-2 overflow-x-auto scrollbar-none pb-2" data-testid="add-catch-angler-list">
              {members.map((m) => (
                <button
                  key={m.id}
                  onClick={() => setOwnerId(m.id)}
                  data-testid={`add-catch-angler-${m.id}`}
                  className={`shrink-0 flex flex-col items-center gap-1 p-2 rounded-2xl transition active:scale-95 ${
                    ownerId === m.id
                      ? "bg-[#E8A838]"
                      : "bg-white border border-[#0A3D62]/10"
                  }`}
                >
                  <div
                    className="w-14 h-14 rounded-full flex items-center justify-center text-3xl"
                    style={{ background: m.color + "33" }}
                  >
                    {m.emoji}
                  </div>
                  <div className="text-xs font-bold text-[#0A3D62] max-w-[70px] truncate">
                    {m.name}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Species */}
          <div>
            <div className="text-xs uppercase font-bold text-[#556B7D] mb-2">Species (optional)</div>
            <div className="flex flex-wrap gap-2" data-testid="add-catch-species-list">
              {SPECIES.map((s) => (
                <button
                  key={s}
                  onClick={() => setSpecies(species === s ? "" : s)}
                  data-testid={`add-catch-species-${s}`}
                  className={`px-4 py-2 rounded-full font-bold text-sm active:scale-95 transition ${
                    species === s
                      ? "bg-[#0A3D62] text-white"
                      : "bg-white text-[#0A3D62] border border-[#0A3D62]/15"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Sticky save */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#FAF8F3] via-[#FAF8F3] to-transparent pt-6 pb-5 px-5">
          <button
            onClick={save}
            disabled={saving}
            data-testid="add-catch-save-btn"
            className="w-full h-16 rounded-2xl bg-[#E8A838] text-[#1A2A35] font-display font-black text-lg flex items-center justify-center gap-2 active:scale-[0.98] disabled:opacity-70"
          >
            {saving ? savingLine : "Log this catch 🎣"}
          </button>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
